import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAccountSettings, useUpdateAccountSettings } from '@/hooks/useAccountSettings'
import { useApiErrorTranslator } from '@/hooks/useApiErrorTranslator'
import { scrollToAndFocusField, type SettingsSaveError } from '@/lib/settingsSaveError'
import type { PaymentMethodConfig, ReceivingAccount } from '@/types'

const defaultPaymentMethods: PaymentMethodConfig = {
  bank_transfer: { enabled: false, account_number: '', account_name: '', bank_name: '' },
  promptpay: { enabled: false, stripe_publishable_key: '', stripe_secret_key: '', stripe_webhook_secret: '' },
  credit_card: { enabled: false, stripe_publishable_key: '', stripe_secret_key: '', stripe_webhook_secret: '' },
  cash: { enabled: false },
  promptpay_bill_payment: { enabled: false, biller_id: '', ref2_mode: 'none', promptpay_proxy_target: '' },
  receiving_accounts: [],
}

export function usePaymentSettings() {
  const { t } = useTranslation('settings')
  const translateError = useApiErrorTranslator('settings')
  const { data: account } = useAccountSettings()
  const updateSettings = useUpdateAccountSettings()

  // null means "not yet edited by user" — use server data
  const [localPaymentMethods, setLocalPaymentMethods] = useState<PaymentMethodConfig | null>(null)
  // EP-143 T143-010 — auto-verify cap is a top-level settings key, not under
  // payment_methods, so it has its own local state slot.
  const [localSlipAutoVerifyMaxAmount, setLocalSlipAutoVerifyMaxAmount] = useState<
    number | null | undefined
  >(undefined)
  const [errors, setErrors] = useState<Record<string, string>>({})
  // PAY-1 (T149-082): set on a failed save (validation stop or server
  // failure) so the caller can render the persistent SettingsSaveAlert;
  // cleared on the next save attempt (success or otherwise).
  const [saveError, setSaveError] = useState<SettingsSaveError | null>(null)

  // Derive current payment methods: prefer local edits, fall back to server data
  const serverPaymentMethods = account?.settings.payment_methods
    ? {
        ...defaultPaymentMethods,
        ...account.settings.payment_methods,
        bank_transfer: {
          ...defaultPaymentMethods.bank_transfer!,
          ...account.settings.payment_methods.bank_transfer,
        },
        promptpay: {
          ...defaultPaymentMethods.promptpay!,
          ...account.settings.payment_methods.promptpay,
        },
        credit_card: {
          ...defaultPaymentMethods.credit_card!,
          ...account.settings.payment_methods.credit_card,
        },
        cash: {
          ...defaultPaymentMethods.cash!,
          ...account.settings.payment_methods.cash,
        },
        promptpay_bill_payment: {
          ...defaultPaymentMethods.promptpay_bill_payment!,
          ...account.settings.payment_methods.promptpay_bill_payment,
        },
        // EP-143 T143-010 — recipient registry
        receiving_accounts: account.settings.payment_methods.receiving_accounts ?? [],
      }
    : defaultPaymentMethods
  const paymentMethods = localPaymentMethods ?? serverPaymentMethods

  // EP-143 T143-010 — auto-verify cap: undefined = not yet touched by user,
  // use server value; null = explicitly cleared; number = new value.
  const slipAutoVerifyMaxAmount =
    localSlipAutoVerifyMaxAmount !== undefined
      ? localSlipAutoVerifyMaxAmount
      : (account?.slip_auto_verify_max_amount ?? null)

  const setPaymentMethods = (updater: PaymentMethodConfig | ((prev: PaymentMethodConfig) => PaymentMethodConfig)) => {
    setLocalPaymentMethods((prev) => {
      const current = prev ?? serverPaymentMethods
      return typeof updater === 'function' ? updater(current) : updater
    })
  }

  const updateBankTransfer = (field: string, value: string | boolean) => {
    setPaymentMethods((prev) => ({
      ...prev,
      bank_transfer: { ...prev.bank_transfer!, [field]: value },
    }))
    setErrors((prev) => ({ ...prev, [field]: '' }))
  }

  const updatePromptpay = (field: string, value: string | boolean) => {
    setPaymentMethods((prev) => ({
      ...prev,
      promptpay: { ...prev.promptpay!, [field]: value },
    }))
    setErrors((prev) => ({ ...prev, [field]: '' }))
  }

  const updateCreditCard = (field: string, value: string | boolean) => {
    setPaymentMethods((prev) => ({
      ...prev,
      credit_card: { ...prev.credit_card!, [field]: value },
    }))
    setErrors((prev) => ({ ...prev, [field]: '' }))
  }

  const updateCash = (field: string, value: boolean) => {
    setPaymentMethods((prev) => ({
      ...prev,
      cash: { ...prev.cash!, [field]: value },
    }))
  }

  const updatePromptpayBillPayment = (field: string, value: string | boolean) => {
    setPaymentMethods((prev) => ({
      ...prev,
      promptpay_bill_payment: { ...prev.promptpay_bill_payment!, [field]: value },
    }))
  }

  // EP-143 T143-010 — receiving accounts CRUD
  const addReceivingAccount = (entry: ReceivingAccount) => {
    setPaymentMethods((prev) => ({
      ...prev,
      receiving_accounts: [...(prev.receiving_accounts ?? []), entry],
    }))
  }

  const updateReceivingAccount = (index: number, entry: ReceivingAccount) => {
    setPaymentMethods((prev) => {
      const updated = [...(prev.receiving_accounts ?? [])]
      updated[index] = entry
      return { ...prev, receiving_accounts: updated }
    })
  }

  const removeReceivingAccount = (index: number) => {
    setPaymentMethods((prev) => {
      const updated = [...(prev.receiving_accounts ?? [])]
      updated.splice(index, 1)
      return { ...prev, receiving_accounts: updated }
    })
  }

  const updateSlipAutoVerifyMaxAmount = (value: number | null) => {
    setLocalSlipAutoVerifyMaxAmount(value)
  }

  // Returns the fresh errors map (not the stale `errors` state: setState
  // doesn't apply synchronously) so handleSave can act on it immediately:
  // the field count and the first invalid field's id, in document order.
  const validate = (): Record<string, string> => {
    const newErrors: Record<string, string> = {}
    const bt = paymentMethods.bank_transfer
    if (bt?.enabled) {
      if (!bt.bank_name?.trim()) newErrors.bank_name = t('payment_methods.validation.required')
      if (!bt.account_number?.trim()) newErrors.account_number = t('payment_methods.validation.required')
      if (!bt.account_name?.trim()) newErrors.account_name = t('payment_methods.validation.required')
    }
    const pp = paymentMethods.promptpay
    if (pp?.enabled) {
      if (!pp.stripe_publishable_key?.trim()) newErrors.promptpay_pk = t('payment_methods.validation.required')
      if (!pp.stripe_secret_key?.trim()) newErrors.promptpay_sk = t('payment_methods.validation.required')
      if (!pp.stripe_webhook_secret?.trim()) newErrors.promptpay_wh = t('payment_methods.validation.required')
    }
    const cc = paymentMethods.credit_card
    if (cc?.enabled) {
      if (!cc.stripe_publishable_key?.trim()) newErrors.credit_card_pk = t('payment_methods.validation.required')
      if (!cc.stripe_secret_key?.trim()) newErrors.credit_card_sk = t('payment_methods.validation.required')
      if (!cc.stripe_webhook_secret?.trim()) newErrors.credit_card_wh = t('payment_methods.validation.required')
    }
    setErrors(newErrors)
    return newErrors
  }

  // PAY-1 (T149-082): returns true only when the save actually happened.
  // A validation stop (false) sets `saveError.fieldCount` and scrolls to +
  // focuses the first invalid field; a server failure (false) sets
  // `saveError.serverMessage`. The caller shows success only on true.
  const handleSave = async (e: React.FormEvent): Promise<boolean> => {
    e.preventDefault()
    const newErrors = validate()
    const fieldCount = Object.keys(newErrors).length
    if (fieldCount > 0) {
      setSaveError({ fieldCount })
      scrollToAndFocusField(Object.keys(newErrors)[0])
      return false
    }
    setSaveError(null)
    try {
      // EP-143 T143-010: save both payment_methods (incl. receiving_accounts)
      // and the top-level slip_auto_verify_max_amount in a single PATCH.
      await updateSettings.mutateAsync({
        settings: {
          payment_methods: paymentMethods,
          ...(localSlipAutoVerifyMaxAmount !== undefined
            ? { slip_auto_verify_max_amount: localSlipAutoVerifyMaxAmount }
            : {}),
        },
      })
      return true
    } catch (err) {
      setSaveError({ serverMessage: translateError(err) })
      return false
    }
  }

  return {
    paymentMethods,
    slipAutoVerifyMaxAmount,
    errors,
    saveError,
    isPending: updateSettings.isPending,
    updateBankTransfer,
    updatePromptpay,
    updateCreditCard,
    updateCash,
    updatePromptpayBillPayment,
    addReceivingAccount,
    updateReceivingAccount,
    removeReceivingAccount,
    updateSlipAutoVerifyMaxAmount,
    handleSave,
  }
}
